import clientFetcher from '@lib/client-fetcher';
import {
  CreateShiftData,
  ReactionType,
  SchoolReactionRequest,
  SchoolReactionResponse,
  Shift,
  ShiftApplicationsResponse,
  ShiftReaction,
  ShiftReactionStatus,
  ShiftResponse,
  ShiftStatus,
  TeacherShift,
  VALID_REACTIONS,
} from 'types/collections';

/**
 * Data structure for updating an existing shift
 * @extends Partial<CreateShiftData> - Allows partial update of shift properties
 */
export interface UpdateShiftData extends Partial<CreateShiftData> {
  status?: ShiftStatus;
}

/**
 * Get all shifts for the school
 */
export class ShiftService {
  static async getShifts(): Promise<Shift[]> {
    return clientFetcher({
      url: '/api/schools/shifts?includeExpired=true',
    });
  }

  /**
   * Get a specific shift by ID
   */
  static async getShift(shiftId: string): Promise<Shift> {
    return clientFetcher({
      url: `/api/schools/shifts/${shiftId}`,
    });
  }

  /**
   * Create a new shift
   */
  static async createShift(shiftData: CreateShiftData): Promise<ShiftResponse> {
    return clientFetcher({
      url: '/api/schools/shifts',
      method: 'POST',
      body: shiftData,
    });
  }

  /**
   * Update an existing shift
   */
  static async updateShift(shiftId: string, updateData: UpdateShiftData): Promise<ShiftResponse> {
    return clientFetcher({
      url: `/api/schools/shifts/${shiftId}`,
      method: 'PATCH',
      body: updateData,
    });
  }

  /**
   * Get all teacher applications for a specific shift
   * @param shiftId - The ID of the shift to get applications for
   * @returns Promise containing shift details and all teacher applications
   */
  static async getShiftApplications(shiftId: string): Promise<ShiftApplicationsResponse> {
    const response: any = await clientFetcher({
      url: `/api/schools/shifts/${shiftId}/reactions`,
    });

    return {
      shift_id: response.shift.id,
      shift_date: response.shift.shift_date,
      time_from: response.shift.time_from,
      time_till: response.shift.time_till,
      group: response.shift.group,
      applications: response.applications || [], // Applications from the API
      total_applications: response.total_applications || 0,
    };
  }

  /**
   * Get shift reactions for teacher
   */
  static async getShiftReactions(): Promise<TeacherShift[]> {
    return clientFetcher({
      url: '/api/teacher/shifts',
    });
  }

  /**
   * Creates a reaction indicating teacher's interest in taking up a shift
   * @param shiftId - The ID of the shift to react to
   * @param validationData - Required validation data including teacherId and shiftStatus
   * @returns Promise The created shift reaction
   */
  static async createShiftReaction(
    shiftId: string,
    validationData: {
      teacherId: string;
      shiftStatus: ShiftStatus;
    },
  ): Promise<ShiftReaction> {
    return clientFetcher({
      url: `/api/teacher/shifts/${shiftId}`,
      method: 'POST',
      body: validationData,
    });
  }

  /**
   * Submit a reaction to a shift (apply or refuse)
   * @param shiftId - The ID of the shift to react to
   * @param reaction - The reaction type (ShiftReactionStatus.APPLIED or ShiftReactionStatus.REFUSED)
   * @param substituteName - Optional name of teacher assistant (when applying)
   * @returns Promise with success message and reaction data
   */
  static async submitShiftReaction(
    shiftId: string,
    reaction: ShiftReactionStatus.APPLIED | ShiftReactionStatus.REFUSED,
    substituteName?: string,
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      reaction: ShiftReaction;
      shift: {
        id: string;
        shift_date: string;
        time_from: string;
        time_till: string;
      };
    };
  }> {
    return clientFetcher({
      url: `/api/teacher/shifts/${shiftId}`,
      method: 'POST',
      body: { reaction, teacher_assistant: substituteName },
    });
  }

  /**
   * School reaction to teacher application for a shift
   * @param shiftId - The ID of the shift
   * @param teacherId - The ID of the teacher who applied
   * @param reaction - The school's reaction (ReactionType)
   * @returns Promise containing the reaction response with notification status
   */
  static async reactToTeacherApplication(
    shiftId: string,
    teacherId: string,
    reaction: ReactionType,
  ): Promise<SchoolReactionResponse> {
    const reactionData: SchoolReactionRequest = { reaction };

    return clientFetcher({
      url: `/api/schools/shifts/${shiftId}/${teacherId}`,
      method: 'POST',
      body: reactionData,
    });
  }

  /**
   * Accept a teacher's application for a shift
   * @param shiftId - The ID of the shift
   * @param teacherId - The ID of the teacher to accept
   * @returns Promise containing the reaction response
   */
  static async acceptTeacherApplication(
    shiftId: string,
    teacherId: string,
  ): Promise<SchoolReactionResponse> {
    return this.reactToTeacherApplication(shiftId, teacherId, VALID_REACTIONS[0]); // 'accepted'
  }

  /**
   * Reject a teacher's application for a shift
   * @param shiftId - The ID of the shift
   * @param teacherId - The ID of the teacher to reject
   * @returns Promise containing the reaction response
   */
  static async rejectTeacherApplication(
    shiftId: string,
    teacherId: string,
  ): Promise<SchoolReactionResponse> {
    return this.reactToTeacherApplication(shiftId, teacherId, VALID_REACTIONS[1]); // 'rejected'
  }
}
